import type { App } from '../app/App';
import type { AgeGroup } from '../game/systems/model';
import { ageGroupFor, isKid } from '../game/systems/policy';
import { language, t } from '../i18n';
import { modal } from './components';
import { h } from './dom';

/**
 * The first-start age screen. Neutral on purpose: no year is preselected and
 * nothing hints at which answer unlocks more. Only the age band is kept.
 * Thai players see Buddhist-era years (พ.ศ.), as on their ID card.
 */
export async function askAge(app: App): Promise<AgeGroup> {
  const now = new Date().getFullYear();
  const era = language() === 'th' ? 543 : 0;
  const select = h(
    'select',
    { class: 'hand-select age-year', 'aria-label': t('Year you were born') },
    h('option', { value: '', selected: true, disabled: true }, t('Pick a year')),
    Array.from({ length: 96 }, (_, i) => now - 4 - i).map((y) => h('option', { value: String(y) }, String(y + era))),
  );
  const hint = h('p', { class: 'hand-hint age-hint', role: 'alert' });
  select.addEventListener('change', () => (hint.textContent = ''));
  const body = h(
    'div',
    { class: 'age-gate' },
    h('p', null, t('Before you open your notebook: what year were you born?')),
    h('label', { class: 'field' }, h('span', null, t('Year you were born')), select),
    hint,
  );
  await modal(t('Welcome to Paper Legends!'), body, [{ label: t('Continue'), value: 'ok', variant: 'primary', color: 'yellow' }], {
    dismissable: false,
    className: 'age-gate-card',
    accept: () => {
      if (select.value) return true;
      hint.textContent = t('Pick the year you were born to continue.');
      return false;
    },
  });
  const group = ageGroupFor(Number(select.value));
  app.store.update(['profile'], (s) => (s.ageGroup = group));
  return group;
}

const NUMBER_WORDS: Record<'en' | 'th', string[]> = {
  en: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'],
  th: ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'],
};

/**
 * Parent gate: a sum written in words that young children cannot easily read.
 * Guards leaving kid mode and, later, anything that spends real money.
 */
export async function parentGate(reason: string): Promise<boolean> {
  const a = 6 + Math.floor(Math.random() * 4);
  const b = 6 + Math.floor(Math.random() * 4);
  const words = NUMBER_WORDS[language()];
  const input = h('input', { class: 'hand-input', inputmode: 'numeric', maxLength: 3, 'aria-label': t('Answer') });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') (input.closest('.modal-card')?.querySelector('.sketch-btn.primary') as HTMLButtonElement | null)?.click();
  });
  const body = h(
    'div',
    { class: 'parent-gate' },
    h('p', null, reason),
    h('p', { class: 'parent-question' }, t('Grown-ups only: what is {a} times {b}?', { a: words[a]!, b: words[b]! })),
    h('label', { class: 'field' }, h('span', null, t('Answer')), input),
  );
  const v = await modal(t('Ask a grown-up'), body, [
    { label: t('Cancel'), value: 'no', variant: 'ghost' },
    { label: t('OK'), value: 'ok', variant: 'primary', color: 'yellow' },
  ]);
  return v === 'ok' && Number(input.value.trim()) === a * b;
}

/**
 * Change the age band from Settings. Moving out of a younger band needs the
 * parent gate; the server account follows (kid mode means a generated name).
 */
export async function changeAge(app: App): Promise<void> {
  if (app.store.state.ageGroup !== 'adult' && !(await parentGate(t('Changing the age setting needs a grown-up.')))) {
    app.toast(t('That answer was not right. Ask a grown-up to help.'), 'info');
    return;
  }
  await askAge(app);
  await syncKidMode(app);
}

/** Make the online account match kid mode (name generated by the server). */
export async function syncKidMode(app: App): Promise<void> {
  const online = app.store.state.online;
  if (!online) return;
  const kid = isKid(app.store.state);
  try {
    app.api.token = online.token;
    const me = await app.api.me();
    if (me.kid === kid) return;
    const p = await app.api.patchMe({ kid, lang: language() });
    app.store.update(['online'], (s) => s.online && (s.online.name = p.name));
  } catch {
    /* offline: ensureOnline syncs it the next time an online feature is used */
  }
}
